/**
 * SEO + JSON-LD pour la page /boutique/[slug] (boutique en ligne).
 * Calqué sur useEventCheckoutSeo (/b) : title enrichi asso, OG image dédiée
 * (/api/og/shop), canonical, noindex sur les statuts non-achetables,
 * BreadcrumbList + ItemList produits.
 */
import type { Shop, ShopProduct, ShopStatus } from "~/types/shop";
import { getDirectusFileId } from "@donoor/types/payment";
import { useDemoAccueil } from "~/composables/useDemoAccueil";

export function useShopCheckoutSeo(
  shop: Ref<Shop | null>,
  slug: Ref<string>,
) {
  const { t } = useI18n();
  const config = useRuntimeConfig();
  // Origine du site COURANT (prod/staging/préview) pour les og:image
  // auto-référentes — cf. useShareBaseUrl. Canonical reste prod en dur.
  const shareBase = useShareBaseUrl();

  const ogDescription = computed(() => {
    if (!shop.value) return "";
    const raw = shop.value.description?.replace(/<[^>]*>/g, "").trim() || "";
    if (raw) return raw.substring(0, 160);
    const org = shop.value.organization_name || t("boutique.commun.associationGenerique");
    return t("boutique.checkoutSeo.descriptionParDefaut", { association: org }).substring(0, 160);
  });

  const shopCoverImageId = computed(() => getDirectusFileId(shop.value?.cover_image));

  // OG image dynamique (pattern /a /b) : /api/og/shop génère un PNG 1200×630.
  const ogImageUrl = computed(() => {
    if (slug.value) return `${shareBase}/api/og/shop?slug=${slug.value}`;
    if (shopCoverImageId.value) {
      return `${config.public.apiUrl}/api/assets/${shopCoverImageId.value}?width=1200&height=630&fit=cover&format=jpg&quality=80`;
    }
    return `${shareBase}/og-image.png`;
  });

  const canonicalUrl = computed(() => `https://donoor.org/boutique/${slug.value}`);

  // Title enrichi : "{Titre} — {Asso} — Boutique" (parité /b : le nom de
  // l'asso est un signal fort pour « boutique {asso} »).
  const seoTitle = computed(() => {
    if (!shop.value) return t("boutique.checkoutSeo.titreDefaut");
    const parts: string[] = [shop.value.title];
    const org = shop.value.organization_name?.trim();
    if (org && !shop.value.title.toLowerCase().includes(org.toLowerCase())) {
      parts.push(org);
    }
    parts.push(t("boutique.checkoutSeo.titreDefaut"));
    return parts.join(" — ").substring(0, 60);
  });

  // Noindex si la boutique n'existe pas ou n'est pas achetable publiquement
  // (même logique que les événements : on ne ranke pas une page non-achetable).
  const shouldNoindex = computed(() => {
    if (!shop.value) return true;
    // Boutique PRIVÉE (migration 285) : accessible par son lien, jamais
    // indexée — c'est sa raison d'être.
    if (shop.value.is_public === false) return true;
    const blockedStatuses: ShopStatus[] = ["draft", "closed", "archived"];
    return blockedStatuses.includes(shop.value.status);
  });

  const robotsContent = useRobotsContent(shouldNoindex);

  // Démo intégrée à l'accueil : la page s'affiche DANS la home, elle ne doit
  // rien écrire dans son <head> (titre, robots, canonique, JSON-LD).
  const resultat = { shopCoverImageId, ogDescription, ogImageUrl, canonicalUrl };
  if (useDemoAccueil().estIntegree) return resultat;

  useHead({
    title: seoTitle,
    bodyAttrs: {
      style: computed(() => (shop.value ? "background-color: #f7f9fd" : "")),
    },
    link: [{ rel: "canonical", key: "canonical", href: canonicalUrl.value }],
    meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
    script: computed(() => {
      if (!shop.value || shouldNoindex.value) return [];

      const orgName = shop.value.organization_name || "Donoor";
      const orgUrl = shop.value.organization_slug
        ? `https://donoor.org/associations/${shop.value.organization_slug}`
        : "https://donoor.org";

      // BreadcrumbList : Accueil → Asso → Boutique
      const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t("boutique.commun.accueil"), item: "https://donoor.org" },
          { "@type": "ListItem", position: 2, name: orgName, item: orgUrl },
          { "@type": "ListItem", position: 3, name: shop.value.title, item: canonicalUrl.value },
        ],
      };

      // ItemList des produits actifs (prix min) — Rich Snippets produits.
      const activeProducts = (shop.value.products || []).filter((p) => p.is_active);
      const productListLd = activeProducts.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: activeProducts.slice(0, 20).map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Product",
                name: p.name,
                ...(p.images?.[0]
                  ? { image: `${config.public.apiUrl}/api/assets/${p.images[0]}?width=600&height=600&fit=cover` }
                  : {}),
                offers: {
                  "@type": "Offer",
                  price: productMinPrice(p),
                  priceCurrency: "EUR",
                  availability: "https://schema.org/InStock",
                  url: canonicalUrl.value,
                },
              },
            })),
          }
        : null;

      return [
        { type: "application/ld+json" as const, innerHTML: JSON.stringify(breadcrumbLd) },
        ...(productListLd
          ? [{ type: "application/ld+json" as const, innerHTML: JSON.stringify(productListLd) }]
          : []),
      ];
    }),
  });

  useSeoMeta({
    title: () => seoTitle.value,
    description: () => ogDescription.value,
    ogType: "website",
    ogTitle: () => shop.value?.title || t("boutique.checkoutSeo.titreDefaut"),
    ogDescription: () => ogDescription.value,
    ogImage: () => ogImageUrl.value,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogUrl: () => canonicalUrl.value,
    ogLocale: "fr_FR",
    twitterCard: "summary_large_image",
    twitterTitle: () => shop.value?.title || t("boutique.checkoutSeo.titreDefaut"),
    twitterDescription: () => ogDescription.value,
    twitterImage: () => ogImageUrl.value,
    robots: () => robotsContent.value,
  });

  return resultat;
}

/** Prix minimum d'un produit toutes variantes confondues (EUROS). */
function productMinPrice(p: ShopProduct): number {
  if (p.pricing === "free") return 0;
  if (p.pricing === "flexible") return Number(p.min_price || 0);
  const base = Number(p.price || 0);
  const overrides = (p.variants || [])
    .filter((v) => v.is_active && v.price_override != null)
    .map((v) => Number(v.price_override));
  return overrides.length > 0 ? Math.min(base, ...overrides) : base;
}
